import { NameEntity } from './name.model';
import { ProductImage } from './product.model';

export interface OrderItem {
  hasPreOrderItems?: boolean;
  id: number;
  customerFullName: string;
  customerEmail?: string;
  purchaseDate: string;
  totalPrice: number;
  status: number; // 0=Pending Payment, 1=Pending, 2=Processing, 3=Ready, 4=Completed, 5=Cancelled, 6=Refunded
  isReady?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Order Detail Interfaces
export interface OrderDetailEntity {
  hasPreOrderItems?: boolean;
  id: number;
  customerFullName: string;
  customerCountryCode?: string | null;
  customerFirstName: string;
  customerLastName: string;
  customerEmail?: string;
  customerPhoneNumber?: string;
  orderDate: string;
  status: number;
  paymentMethod?: NameEntity;
  paymentMethodId?: number;
  couponCode?: string | null;
  orderNote?: string | null; // Order note from customer
  address?: OrderAddress;
  items?: OrderItemDetail[];
  prices?: OrderPricing;
  logs?: OrderLog[];
  isGuest?: boolean;
  shouldPromptWalletRefund?: boolean;
}

export interface OrderAddress {
  address?: string;
  id: number;
  name: string;
  governorate: NameEntity;
  governorateId: number;
  area: NameEntity;
  areaId: number;
  block?: string;
  street?: string;
  avenue?: string;
  type: number;
  buildingNumber?: string;
  floor?: string;
  apartment?: string;
  lat?: number;
  long?: number;
}

export interface OrderItemDetail {
  isPreOrder?: boolean;
  preOrderAvailableOn?: string | null;
  variantId: number;
  productName: NameEntity;
  productSku: string;
  productImage: ProductImage;
  variationTerms: string[];
  orderedQuantity: number;
  productPrice?: number | null;
  productVendorName: string;
  variantOrderStatus: OrderItemStatus;
}

export interface OrderPricing {
  itemsPrice: number;
  itemsDiscount: number;
  shippingFee: number;
  shippingFeeDiscount: number;
  totalCouponCodeDiscount: number;
  totalOrderPrice: number;
}

export interface OrderLog {
  date: string;
  action: string;
  from: string;
  to: string;
  user: string;
}

// Update interfaces
export interface OrderStatusUpdate {
  orderId: number;
  status: number;
}

export interface OrderCustomerUpdate {
  orderId: number;
  fields: {
    fullName?: string;
    countryCode?: string | null;
    address?: string;
    firstName?: string,
    lastName?: string,
    email?: string;
    phoneNumber?: string;
    shippingCost?: number;
    governorateId?: number;
    areaId?: number;
    block?: string;
    street?: string;
    avenue?: string;
    type?: number;
    buildingNumber?: string;
    floor?: string;
    apartment?: string;
    lat?: number;
    long?: number;
  };
}

// Vendor-specific interfaces based on updated API documentation
export interface VendorOrderItemDetail {
  id?: number; // Variant ID for API operations
  variantId?: number; // Alternative field name for variant ID
  productName: NameEntity;
  variationTerms: Array<{
    term: NameEntity;
  }>;
  orderedQuantity: number;
  productPrice?: number | null;
  vendorOrderStatus: OrderItemStatus; // Status values from API response (0=InProgress, 1=Ready)
}

export interface VendorOrderDetailEntity {
  id: number;
  orderDate: string;
  status: number; // OrderStatus enum
  orderNote?: string | null; // Order note from customer
  items?: VendorOrderItemDetail[];
  prices?: VendorOrderPricing;
}

export interface VendorOrderPricing {
  itemsPrice: number; // Total price of vendor's items
  itemsDiscount: number; // Discount on vendor's items
  totalCouponCodeDiscount: number; // Coupon discount applied to vendor's items
  totalOrderPrice: number; // Total amount for vendor's items
}

// Vendor order item actions
export interface OrderItemStatusUpdate {
  orderItemId: number;
  status: string; // "Pending", "Ready", "Cancelled"
}

// Vendor order state enum
export enum OrderItemStatus {
  InProgress = 0,
  Ready = 1,
  Cancelled = 2
}

// variant status update request
export interface VariantStatusUpdate {
  orderId: number;
  variantIds: number[];
  status: OrderItemStatus;
}

// Payment method from API (PaymentMethodViewDTO)
export interface PaymentMethodView {
  id: number;
  name: string;
  image: {
    path: string;
  };
}

// Order pricing interface for order summary
export interface OrderPrices {
  itemsPrice: number;
  itemsDiscount: number;
  shippingFee: number;
  shippingFeeDiscount: number;
  totalCouponCodeDiscount: number;
  totalOrderPrice: number;
}

// Order summary response from PreviewCheckout API
export interface OrderSummaryResponse {
  prices: OrderPrices;
  paymentMethods: PaymentMethodView[];
  walletBalance?: number;
}

// Order Item Report interfaces
export interface OrderItemReportDTO {
  orderId: number;
  orderTime: string;
  image?: ProductImage;
  arabicProductName: string;
  englishProductName: string;
  sku: string;
  brand?: string;
  primaryCategory?: string;
  vendor: string;
  soldPrice: number;
  quantity: number;
  itemCode?: string;
  article?: string;
}

export interface OrderItemReportResponseDTO {
  items: OrderItemReportDTO[];
  totalQuantity: number;
  totalPrice: number;
}
